import type { Dispatch, UnknownAction } from '@reduxjs/toolkit'

import { defineListener, errorView, type ModuleListener } from '@krp/client-runtime'

import { classifyCommandError } from '../../../shared/model/commandOutcome'
import { onecStructure } from '../../structure/api/structureApi'
import { selectStructureWarehouses } from '../../structure/model/selectors'
import {
  inventoryClearRequested,
  inventoryCreateRequested,
  inventoryOperationResendRequested,
  inventoryReloadRequested,
  inventorySaveRequested,
  inventoryScanRequested,
} from './commands'
import {
  countChanged,
  EDIT_ACTIONS,
  operationDismissed,
  operationPrepared,
  operationResent,
  operationSettled,
  operationStarted,
  rowReplaced,
  rowAdded,
  saveFailed,
  saveStarted,
  saveSucceeded,
  scanSettled,
  scanStarted,
  sessionCleared,
} from './inventorySlice'
import { createKey, inventoryIdempotencyKey, normalizeRid, sessionKey, type InventoryCommandAction } from './keys'
import type { InventoryPorts, InventoryPortsFactory } from './ports'
import { mergeRows } from './rows'
import { warehouseChoiceDropped, warehouseChosen, type WarehouseChoiceState } from './warehouseChoiceSlice'
import type { ClearOperation, CreateOperation, InventoryIntentState, InventoryOperation, SessionDelta, SessionRow } from './types'

interface Scenario {
  ports: InventoryPorts
  dispatch: Dispatch<UnknownAction>
  intent: () => InventoryIntentState
  choice: () => WarehouseChoiceState
  /** Отложить сохранение склада: каждая новая правка сдвигает его на паузу. */
  schedule: (target: SessionRef, delayMs: number) => void
  /** Снять отложенное сохранение склада (сессия очищена). */
  cancel: (target: SessionRef) => void
}

interface SessionRef {
  resourceId: string
  warehouseRefKey: string
}

const ref = (payload: SessionRef): SessionRef => ({
  resourceId: normalizeRid(payload.resourceId),
  warehouseRefKey: payload.warehouseRefKey.trim().toLowerCase(),
})

/**
 * Операция всё ещё ЭТА: её не сменила вторая команда, не стёр выход
 * пользователя или очистка базы. Проверяется после каждого `await`: иначе
 * воскресшая команда ушла бы в 1С от имени следующего человека.
 */
function owns(scenario: Scenario, operation: InventoryOperation): boolean {
  const intent = scenario.intent()
  const current = intent.creates[operation.key] ?? intent.clears[operation.key]
  return current !== undefined && current.operationId === operation.operationId && current.status === 'sending'
}

function settle(scenario: Scenario, operation: InventoryOperation, raw: unknown, beforeSend = false) {
  const failure = classifyCommandError(raw)
  scenario.dispatch(
    operationSettled({
      key: operation.key,
      operationId: operation.operationId,
      // Отказ ЧТЕНИЯ до команды: в 1С ничего не уходило — «не выполнено», а не «неизвестно».
      status: beforeSend && failure.status === 'unknown' ? 'failed' : failure.status,
      resendable: beforeSend ? false : failure.resendable,
      error: failure.error,
    }),
  )
}

/**
 * Строка найденного товара, которую человек сейчас видит на складе: сохранённые строки сервера с наложенной
 * несохранённой правкой. Сессия читается с сервера (в 1С не ходит); не прочиталась — повторов не ищем, скан
 * добавит строку, как раньше.
 */
async function visibleRow(scenario: Scenario, target: SessionRef, productRefKey: string): Promise<SessionRow | undefined> {
  const key = sessionKey(target.resourceId, target.warehouseRefKey)
  const draft = scenario.intent().drafts[key]
  const delta: SessionDelta = draft === undefined ? { added: [], changes: {}, removed: [] } : draft
  const local = mergeRows([], delta).find((row) => row.found && row.productRefKey === productRefKey)
  if (local !== undefined) return local
  const current = await scenario.ports.readSession(target.resourceId, target.warehouseRefKey)
  if (!current.ok) return undefined
  const latest = scenario.intent().drafts[key] ?? delta
  return mergeRows(current.value.data?.rows ?? [], latest).find((row) => row.found && row.productRefKey === productRefKey)
}

// --- сохранение -------------------------------------------------------------------------------------------------

async function save(scenario: Scenario, target: SessionRef) {
  const { ports, dispatch } = scenario
  const key = sessionKey(target.resourceId, target.warehouseRefKey)
  const draft = scenario.intent().drafts[key]
  // Одно сохранение склада за раз: иначе более старый набор мог бы прийти на сервер последним.
  if (draft === undefined || draft.save.status === 'sending') return
  // Очистка в пути или с неизвестным исходом: набор поверх очищаемой сессии не отправляется.
  const clear = scenario.intent().clears[key]
  if (clear !== undefined && (clear.status === 'sending' || clear.status === 'unknown')) return
  const operationId = ports.newId()
  const sent: SessionDelta = { added: draft.added, changes: draft.changes, removed: draft.removed }
  dispatch(saveStarted({ ...target, operationId, revision: draft.revision }))
  const ours = () => scenario.intent().drafts[key]?.save.operationId === operationId

  const current = await ports.readSession(target.resourceId, target.warehouseRefKey)
  if (!ours()) return
  if (!current.ok) {
    dispatch(saveFailed({ ...target, operationId, status: 'failed', error: errorView(current.error) }))
    return
  }
  const server = current.value.data
  const choice = scenario.choice()[target.resourceId]
  const chosen = choice?.refKey === target.warehouseRefKey ? choice : undefined
  const outcome = await ports.saveSession(
    target.resourceId,
    {
      operationId,
      idempotencyKey: inventoryIdempotencyKey('save', operationId),
      expectedConfigurationVersion: current.value.configurationVersion,
    },
    {
      warehouseRefKey: target.warehouseRefKey,
      warehouseName: server?.warehouseName ?? chosen?.name ?? '',
      rows: mergeRows(server?.rows ?? [], sent),
    },
  )
  if (!ours()) return
  if (outcome.ok) {
    // Сначала кэш, потом дельта: иначе на миг пропали бы только что подтверждённые строки.
    ports.storeSession(target.resourceId, target.warehouseRefKey, outcome.value)
    dispatch(saveSucceeded({ ...target, operationId, sent, savedAt: ports.now() }))
    if (scenario.intent().drafts[key]?.save.status === 'pending') scenario.schedule(target, 0)
    return
  }
  const failure = classifyCommandError(outcome.error)
  // PUT сессии — замена набора целиком; повтор не удвоит строки, но сам он не делается: решает человек.
  dispatch(
    saveFailed({ ...target, operationId, status: failure.status === 'unknown' ? 'unknown' : 'failed', error: failure.error }),
  )
  // Настройки базы или сессия изменились: перечитать — следующее сохранение пойдёт по новым.
  if (failure.status === 'conflict' || failure.error.kind === 'conflict') ports.reloadSession(target.resourceId, target.warehouseRefKey)
}

// --- создание товара и очистка ------------------------------------------------------------------------------------

async function sendCreate(scenario: Scenario, operation: CreateOperation) {
  if (!owns(scenario, operation)) return
  const outcome = await scenario.ports.createProduct(operation)
  if (!owns(scenario, operation)) return
  if (!outcome.ok) {
    settle(scenario, operation, outcome.error)
    return
  }
  const created = outcome.value
  if (!created.found || created.productRefKey === null) {
    scenario.dispatch(
      operationSettled({
        key: operation.key,
        operationId: operation.operationId,
        status: 'failed',
        resendable: false,
        error: errorView({ message: 'Служба не вернула созданный товар' }),
      }),
    )
    return
  }
  const target = { resourceId: operation.resourceId, warehouseRefKey: operation.warehouseRefKey }
  scenario.dispatch(
    rowReplaced({
      ...target,
      oldId: operation.payload.rowId,
      row: { ...created, id: scenario.ports.newId(), counted: null, reprint: false },
    }),
  )
  scenario.dispatch(
    operationSettled({ key: operation.key, operationId: operation.operationId, status: 'succeeded', resendable: false, error: null }),
  )
  // Строка «не найден» заменена: итог создания показывать больше негде.
  scenario.dispatch(operationDismissed({ key: operation.key }))
}

async function sendClear(scenario: Scenario, operation: ClearOperation) {
  if (!owns(scenario, operation)) return
  const outcome = await scenario.ports.clearSession(operation)
  if (!owns(scenario, operation)) return
  if (!outcome.ok) {
    settle(scenario, operation, outcome.error)
    return
  }
  const target = { resourceId: operation.resourceId, warehouseRefKey: operation.warehouseRefKey }
  scenario.cancel(target)
  scenario.ports.storeSession(operation.resourceId, operation.warehouseRefKey, {
    resourceId: operation.resourceId,
    configurationVersion: operation.expectedConfigurationVersion,
    data: null,
  })
  scenario.dispatch(sessionCleared(target))
  scenario.dispatch(
    operationSettled({ key: operation.key, operationId: operation.operationId, status: 'succeeded', resendable: false, error: null }),
  )
}

/** Начать команду: она встаёт «в пути» СРАЗУ, второе нажатие видит занятость. Потом — ревизия базы. */
async function begin<P>(
  scenario: Scenario,
  operation: InventoryOperation<P>,
  started: UnknownAction,
): Promise<InventoryOperation<P> | undefined> {
  scenario.dispatch(started)
  if (!owns(scenario, operation)) return undefined
  const session = await scenario.ports.readSession(operation.resourceId, operation.warehouseRefKey)
  if (!owns(scenario, operation)) return undefined
  if (!session.ok) {
    settle(scenario, operation, session.error, true)
    return undefined
  }
  const expectedConfigurationVersion = session.value.configurationVersion
  scenario.dispatch(operationPrepared({ key: operation.key, operationId: operation.operationId, expectedConfigurationVersion }))
  return { ...operation, expectedConfigurationVersion }
}

function newOperation<P>(scenario: Scenario, key: string, target: SessionRef, action: InventoryCommandAction, payload: P): InventoryOperation<P> {
  const operationId = scenario.ports.newId()
  return {
    key,
    ...target,
    operationId,
    // Ключ привязан к операции: одинаковые ключи в двух базах — две операции и на сервере.
    idempotencyKey: inventoryIdempotencyKey(action, operationId),
    expectedConfigurationVersion: '',
    payload,
    status: 'sending',
    resendable: false,
    error: null,
  }
}

/** Ответ чтения структуры базы 1С (в том числе положенный в кэш «Обновить из 1С»). */
type StructureAnswered = typeof onecStructure.matchFulfilled extends (action: unknown) => action is infer A ? A : never

/** Listeners фичи: команда → порт. Состояние модуля читают только через `access`. */
export interface InventoryStateAccess<S> {
  intent: (state: S) => InventoryIntentState
  choice: (state: S) => WarehouseChoiceState
}

export function createInventoryListeners<S>(makePorts: InventoryPortsFactory, access: InventoryStateAccess<S>): ModuleListener<S>[] {
  // Таймеры автосохранения по ключу сессии — свои у каждого модуля (стора).
  const timers = new Map<string, ReturnType<typeof setTimeout>>()
  const scenarioOf = (api: { dispatch: Dispatch<UnknownAction>; getModuleState: () => S }): Scenario => ({
    ports: makePorts(api.dispatch),
    dispatch: api.dispatch,
    intent: () => access.intent(api.getModuleState()),
    choice: () => access.choice(api.getModuleState()),
    cancel: (target) => cancel(target),
    schedule: (target, delayMs) => {
      const key = sessionKey(target.resourceId, target.warehouseRefKey)
      const previous = timers.get(key)
      if (previous !== undefined) clearTimeout(previous)
      timers.set(
        key,
        setTimeout(() => {
          timers.delete(key)
          api.dispatch(inventorySaveRequested(target))
        }, delayMs),
      )
    },
  })
  const cancel = (target: SessionRef) => {
    const key = sessionKey(target.resourceId, target.warehouseRefKey)
    const previous = timers.get(key)
    if (previous !== undefined) clearTimeout(previous)
    timers.delete(key)
  }
  const editListeners = EDIT_ACTIONS.map((creator) =>
    defineListener<S, ReturnType<typeof creator>>(creator.match, (action, api) => {
      const scenario = scenarioOf(api)
      scenario.schedule(ref(action.payload), scenario.ports.debounceMs)
    }),
  )
  return [
    ...editListeners,

    defineListener<S, ReturnType<typeof inventorySaveRequested>>(inventorySaveRequested.match, async (action, api) => {
      await save(scenarioOf(api), ref(action.payload))
    }),

    defineListener<S, ReturnType<typeof inventoryScanRequested>>(inventoryScanRequested.match, async (action, api) => {
      const scenario = scenarioOf(api)
      const target = ref(action.payload)
      const barcode = action.payload.barcode.trim()
      const key = sessionKey(target.resourceId, target.warehouseRefKey)
      if (barcode === '' || scenario.intent().scans[key]?.busy === true) return
      scenario.dispatch(scanStarted(target))
      const outcome = await scenario.ports.scan(target.resourceId, target.warehouseRefKey, barcode)
      // Выход пользователя или очистка базы во время скана: строку не добавлять.
      if (scenario.intent().scans[key]?.busy !== true) return
      if (outcome.ok) {
        // Полку считают коробками: берут по одной и сканируют каждую. Повторный скан найденного товара прибавляет к
        // «Посчитано», а не заводит вторую карточку: дубли пришлось бы складывать глазами, а отклонение каждой
        // считалось бы от полного остатка (так было в прежнем клиенте; мобильный аудит 15.10, п. 19).
        // Ненайденные коды остаются отдельными строками: складывать их не по чему.
        const same = outcome.value.found && outcome.value.productRefKey !== null ? await visibleRow(scenario, target, outcome.value.productRefKey) : undefined
        if (scenario.intent().scans[key]?.busy !== true) return
        if (same !== undefined) {
          scenario.dispatch(countChanged({ ...target, rowId: same.id, counted: (same.counted ?? 1) + 1 }))
        } else {
          scenario.dispatch(rowAdded({ ...target, row: { ...outcome.value, id: scenario.ports.newId(), counted: null, reprint: false } }))
        }
        scenario.dispatch(scanSettled({ ...target, error: null }))
      } else {
        scenario.dispatch(scanSettled({ ...target, error: errorView(outcome.error) }))
      }
    }),

    defineListener<S, ReturnType<typeof inventoryCreateRequested>>(inventoryCreateRequested.match, async (action, api) => {
      const scenario = scenarioOf(api)
      const target = ref(action.payload)
      const name = action.payload.name.trim()
      const barcode = action.payload.barcode.trim()
      if (name === '' || barcode === '') return
      const key = createKey(target.resourceId, target.warehouseRefKey, action.payload.rowId)
      const created: CreateOperation = newOperation(scenario, key, target, 'create', {
        rowId: action.payload.rowId,
        name,
        barcode,
        prices: [...action.payload.prices],
      })
      const operation = await begin(scenario, created, operationStarted({ kind: 'create', operation: created }))
      if (operation !== undefined) await sendCreate(scenario, operation)
    }),

    defineListener<S, ReturnType<typeof inventoryClearRequested>>(inventoryClearRequested.match, async (action, api) => {
      const scenario = scenarioOf(api)
      const target = ref(action.payload)
      const key = sessionKey(target.resourceId, target.warehouseRefKey)
      // Пока дельта сохраняется, очистка ждёт: иначе ответ сохранения вернул бы строки после очистки.
      if (scenario.intent().drafts[key]?.save.status === 'sending') return
      const cleared: ClearOperation = newOperation(scenario, key, target, 'clear', null)
      const operation = await begin(scenario, cleared, operationStarted({ kind: 'clear', operation: cleared }))
      if (operation !== undefined) await sendClear(scenario, operation)
    }),

    defineListener<S, ReturnType<typeof inventoryOperationResendRequested>>(
      inventoryOperationResendRequested.match,
      async (action, api) => {
        const scenario = scenarioOf(api)
        const intent = scenario.intent()
        const create = intent.creates[action.payload.key]
        const clear = intent.clears[action.payload.key]
        const operation = create ?? clear
        // Повтор — только явным действием человека и только той же командой с тем же ключом.
        if (operation === undefined || operation.status === 'sending' || !operation.resendable) return
        scenario.dispatch(operationResent({ key: operation.key }))
        if (create !== undefined) await sendCreate(scenario, { ...create, status: 'sending' })
        else if (clear !== undefined) await sendClear(scenario, { ...clear, status: 'sending' })
      },
    ),

    /**
     * Ответ живой структуры 1С сверяет сохранённый выбор склада базы (RP-LSB2):
     * имя расходится — запоминается имя из 1С (его же увидит следующий F5 до
     * ответа), склада больше нет — выбор снимается. В 1С здесь ничего не уходит.
     */
    defineListener<S, StructureAnswered>(onecStructure.matchFulfilled, (action, api) => {
      const resourceId = normalizeRid(action.meta.arg.originalArgs.resourceId)
      const chosen = access.choice(api.getModuleState())[resourceId]
      if (chosen === undefined) return
      const live = selectStructureWarehouses(action.payload.data).find((item) => item.refKey === chosen.refKey)
      if (live === undefined) api.dispatch(warehouseChoiceDropped({ resourceId, warehouse: chosen }))
      else if (live.name !== chosen.name) api.dispatch(warehouseChosen({ resourceId, warehouse: { refKey: live.refKey, name: live.name } }))
    }),

    defineListener<S, ReturnType<typeof inventoryReloadRequested>>(inventoryReloadRequested.match, (action, api) => {
      const ports = makePorts(api.dispatch)
      if (action.payload.warehouseRefKey === null) ports.reloadWarehouses(action.payload.resourceId)
      else ports.reloadSession(action.payload.resourceId, action.payload.warehouseRefKey)
    }),
  ]
}

